'use client';

import {
  useActiveWallet,
  useInvalidateWalletCache,
  useRegisterWallet,
  useSdkTurnkey,
  useWallets,
} from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { useAnnouncer } from '@/components/ui/announcer';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { runWithCleanup } from '@/lib/async/run-with-cleanup';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';

interface ConnectWalletProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called before Turnkey's modal opens so parent modals can dismiss. */
  onBeforeTurnkeyModal?: () => void;
}

/**
 * Connect an external wallet (Phantom, Solflare, etc.) to the authenticated
 * user's account. Delegates entirely to Turnkey's managed
 * `handleConnectExternalWallet()` modal for provider selection and signature
 * verification, then registers the wallet with the Morfi backend.
 *
 * This component renders no UI of its own — it triggers Turnkey's modal
 * imperatively when `open` transitions to `true`.
 */
export function ConnectWallet({
  open,
  onOpenChange,
  onBeforeTurnkeyModal,
}: ConnectWalletProps): null {
  const t = useTranslations('wallet.connect');
  const { announce } = useAnnouncer();
  const { handleConnectExternalWallet, closeModal: closeTurnkeyModal } = useSdkTurnkey();
  const { wallets, unlinkWallet } = useWallets();
  const invalidateWalletCache = useInvalidateWalletCache();
  const { mutateAsync: registerWallet } = useRegisterWallet();
  const { setActiveWalletId } = useActiveWallet();
  const { enabled: multiWalletsEnabled, canStartOperation } = useMultiWalletFeature();
  const connectingRef = useRef(false);
  const wasMultiWalletsEnabledRef = useRef(multiWalletsEnabled);

  useEffect(() => {
    const gateTurnedOff = wasMultiWalletsEnabledRef.current && !multiWalletsEnabled;
    wasMultiWalletsEnabledRef.current = multiWalletsEnabled;
    if (multiWalletsEnabled || (!open && !gateTurnedOff)) return;
    closeTurnkeyModal();
    if (open) onOpenChange(false);
  }, [closeTurnkeyModal, multiWalletsEnabled, onOpenChange, open]);

  useEffect(() => {
    if (!open || connectingRef.current) return;
    if (!canStartOperation('external_connect', 'wallet_management')) {
      closeTurnkeyModal();
      onOpenChange(false);
      return;
    }

    connectingRef.current = true;

    // Close parent modals before Turnkey's modal opens
    onOpenChange(false);
    onBeforeTurnkeyModal?.();

    async function connect(): Promise<void> {
      await runWithCleanup(
        async () => {
          try {
            const result = await handleConnectExternalWallet();

            // handleConnectExternalWallet returns void if cancelled
            if (!result) return;

            const operation =
              result.type === 'disconnect' ? 'external_disconnect' : 'external_connect';
            if (!canStartOperation(operation, 'wallet_management')) {
              closeTurnkeyModal();
              return;
            }

            // Handle disconnect — Turnkey's modal shows a "Disconnect" option for
            // already-connected wallets. When the user disconnects, remove from our DB.
            if (result.type === 'disconnect') {
              const disconnectedAddress = result.account?.address;
              if (disconnectedAddress) {
                const match = wallets.find(
                  (w) => w.address.toLowerCase() === disconnectedAddress.toLowerCase(),
                );
                if (match) {
                  await unlinkWallet(match.id);
                  showToast({
                    id: TOAST_IDS.WALLET_CONNECTED,
                    message: t('disconnected'),
                    variant: 'success',
                  });
                }
              }
              return;
            }

            if (result.type !== 'connect') return;

            const address: string = result.account.address;
            const providerName: string = result.account.walletAccountId.split('-')[0] ?? 'External';
            const label: string = providerName.charAt(0).toUpperCase() + providerName.slice(1);

            // Check if this wallet is already registered in our DB (by the current user).
            // Turnkey's connected wallets are client-side only — they don't know about
            // our backend, so re-connecting a previously registered wallet is expected.
            const alreadyRegistered = wallets.some(
              (w) => w.address.toLowerCase() === address.toLowerCase(),
            );

            let walletId: string;
            if (!alreadyRegistered) {
              const registered = (await registerWallet({
                publicKey: address,
                walletType: 'external',
                label,
              })) as { id: string; publicKey: string };
              walletId = registered.id;
            } else {
              const existing = wallets.find(
                (w) => w.address.toLowerCase() === address.toLowerCase(),
              );
              walletId = existing?.id ?? '';
            }

            await invalidateWalletCache();

            if (walletId) {
              setActiveWalletId(walletId);
            }

            onEvent('wallet_connected', {
              wallet_type: 'external',
              address,
            });

            announce(t('connected'));
            showToast({
              id: TOAST_IDS.WALLET_CONNECTED,
              message: t('connected'),
              variant: 'success',
            });
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            logger.warn('Wallet connection error', { error: msg.slice(0, 200) });
            const lc = msg.toLowerCase();
            const isCancellation =
              lc.includes('cancel') ||
              lc.includes('closed') ||
              lc.includes('dismissed') ||
              lc.includes('rejected');
            if (isCancellation) return;

            // "Already linked to another user" is a real error — different from
            // "already registered by the current user" which we handle above.
            const isAlreadyLinked =
              lc.includes('already linked') || lc.includes('wallet_already_linked');

            showToast({
              id: TOAST_IDS.WALLET_CONNECTED,
              message: isAlreadyLinked ? t('error_already_linked') : t('error_generic'),
              variant: 'error',
            });
          }
        },
        () => {
          connectingRef.current = false;
        },
      );
    }

    connect();
  }, [
    open,
    onOpenChange,
    onBeforeTurnkeyModal,
    handleConnectExternalWallet,
    wallets,
    invalidateWalletCache,
    announce,
    t,
    setActiveWalletId,
    unlinkWallet,
    registerWallet,
    canStartOperation,
    closeTurnkeyModal,
  ]);

  return null;
}
